import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { IMAGES, bgStyle } from '@/config/images';

const MAX_BYTES = 2 * 1024 * 1024; // 2MB
const GCODE_EXTS = ['.nc', '.tap', '.gcode', '.txt'];

function stripDataUrlPrefix(dataUrl) {
  const idx = dataUrl.indexOf(',');
  return idx === -1 ? dataUrl : dataUrl.slice(idx + 1);
}

const GCodeMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [machines, setMachines] = useState([]);
  const [machineId, setMachineId] = useState('');

  const [gcode, setGcode] = useState('');
  const [review, setReview] = useState(null);
  const [optimized, setOptimized] = useState('');
  const [changeLog, setChangeLog] = useState([]);
  const [warnings, setWarnings] = useState([]);

  const [busy, setBusy] = useState(false);

  const [images, setImages] = useState([]); // {mime_type, data_base64, label}

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveFilename, setSaveFilename] = useState('');
  const [saveNotes, setSaveNotes] = useState('');

  const selectedMachine = useMemo(() => machines.find((m) => m.id === machineId), [machines, machineId]);
  const controllerType = (selectedMachine?.controller_board || '').trim();
  const reviewControllerInfo = controllerType && controllerType.toLowerCase() !== 'unknown' ? controllerType : '';

  useEffect(() => {
    const loadMachines = async () => {
      try {
        const res = await axios.get(`${API}/machines`);
        const list = res.data || [];
        setMachines(list);
        if (list.length > 0) setMachineId(list[0].id);
      } catch (e) {
        console.error('Failed to load machines', e);
      }
    };
    loadMachines();
  }, []);

  useEffect(() => {
    const id = location.state?.duplicateFromId;
    if (!id) return;

    const run = async () => {
      try {
        const res = await axios.get(`${API}/gcode/${id}`);
        setGcode(res.data.original_gcode || '');
        setOptimized(res.data.optimized_gcode || '');
        setReview(null);
        setChangeLog([]);
        setWarnings([]);
        if (res.data.machine_profile_id) setMachineId(res.data.machine_profile_id);
        setSaveFilename(`Copy - ${res.data.filename || 'Untitled'}`);
        toast.success('Loaded G-code into editor');
        navigate('/tools/gcode-master', { replace: true, state: {} });
      } catch (e) {
        toast.error('Failed to load file');
      }
    };

    run();
  }, [location.state, navigate]);

  const handleGcodeFile = async (file) => {
    if (!file) return;

    const name = file.name || '';
    const lower = name.toLowerCase();
    if (!GCODE_EXTS.some((ext) => lower.endsWith(ext))) {
      toast.error('Unsupported file type. Use .nc, .tap, .gcode, or .txt');
      return;
    }

    if (file.size > MAX_BYTES) {
      toast.error('File is too large (max 2MB)');
      return;
    }

    const text = await file.text();
    if (text.includes('\u0000')) {
      toast.error('Binary files are not supported');
      return;
    }

    setGcode(text);
    setReview(null);
    setOptimized('');
    setChangeLog([]);
    setWarnings([]);
    if (!saveFilename) setSaveFilename(name);
  };

  const handleImageFiles = async (fileList, label) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    if (files.some((f) => f.size > MAX_BYTES)) {
      toast.error('One of the images is too large (max 2MB each)');
      return;
    }

    const allowed = new Set(['image/jpeg', 'image/png', 'image/webp']);
    if (files.some((f) => !allowed.has(f.type))) {
      toast.error('Images must be JPG, PNG, or WEBP');
      return;
    }

    const toAdd = await Promise.all(
      files.slice(0, 3).map(
        (f) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                mime_type: f.type,
                data_base64: stripDataUrlPrefix(String(reader.result || '')),
                label,
              });
            };
            reader.readAsDataURL(f);
          })
      )
    );

    setImages((prev) => [...(prev || []), ...toAdd]);
  };

  const onReview = async () => {
    if (!gcode.trim()) {
      toast.error('Paste or upload G-code first');
      return;
    }

    if (!machineId) {
      toast.error('Please select a machine profile');
      return;
    }

    setBusy(true);
    try {
      const res = await axios.post(`${API}/gcode/review`, {
        gcode,
        machine_profile_id: machineId,
        images,
      });
      setReview(res.data);
      toast.success('Review complete');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Review failed');
    } finally {
      setBusy(false);
    }
  };

  const onOptimize = async () => {
    if (!gcode.trim()) {
      toast.error('Paste or upload G-code first');
      return;
    }

    if (!machineId) {
      toast.error('Please select a machine profile');
      return;
    }

    setBusy(true);
    try {
      const res = await axios.post(`${API}/gcode/optimize`, {
        gcode,
        machine_profile_id: machineId,
        images,
      });
      setOptimized(res.data.optimized_gcode || '');
      setChangeLog(res.data.change_log || []);
      setWarnings(res.data.warnings || []);
      toast.success('Optimization complete');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Optimization failed');
    } finally {
      setBusy(false);
    }
  };

  const downloadText = (text, filename) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const openSave = () => {
    if (!optimized.trim()) {
      toast.error('Generate optimized G-code first');
      return;
    }
    if (!saveFilename) setSaveFilename('optimized.gcode');
    setSaveOpen(true);
  };

  const onSave = async () => {
    if (!saveFilename.trim()) {
      toast.error('Filename is required');
      return;
    }

    setBusy(true);
    try {
      await axios.post(`${API}/gcode/save`, {
        filename: saveFilename.trim(),
        original_gcode: gcode,
        optimized_gcode: optimized,
        machine_profile_id: machineId,
        controller_type: reviewControllerInfo || null,
        notes: saveNotes || null,
      });
      toast.success('Saved to My G-Code Library');
      setSaveOpen(false);
      setSaveNotes('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" style={bgStyle(IMAGES.GCODE_CAM)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
    <div className="space-y-6" data-testid="gcode-master-page">
      <div className="panel-header">
        <div>
          <div className="panel-title">Tools / G-Code Master</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">G-Code Master</h1>
          <p className="text-sm md:text-base text-[var(--text-secondary)] mt-2 max-w-2xl">
            Review and optimize G-code with your machine profile. Controller type is pulled from Machine Setup.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/profile/gcode-library')}>
            My G-Code Library
          </Button>
        </div>
      </div>

      <div className="panel space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Machine profile</Label>
            <Select value={machineId} onValueChange={setMachineId}>
              <SelectTrigger>
                <SelectValue placeholder="Select machine" />
              </SelectTrigger>
              <SelectContent>
                {machines.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-[var(--text-secondary)]">
              Controller: <span className="font-mono">{reviewControllerInfo || 'not set (review uses generic rules)'}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Upload G-code file</Label>
            <Input
              type="file"
              accept={GCODE_EXTS.join(',')}
              onChange={(e) => handleGcodeFile(e.target.files?.[0])}
            />
            <div className="text-xs text-[var(--text-secondary)]">Max size 2MB. Supported: .nc .tap .gcode .txt</div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Paste G-code</Label>
          <Textarea
            value={gcode}
            onChange={(e) => setGcode(e.target.value)}
            className="min-h-[240px] font-mono"
            placeholder="Paste your G-code here..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Optional: Machine photo(s)</Label>
            <Input type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={(e) => handleImageFiles(e.target.files, 'machine')} />
          </div>
          <div className="space-y-2">
            <Label>Optional: Controller board photo(s)</Label>
            <Input type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={(e) => handleImageFiles(e.target.files, 'controller')} />
          </div>
          <div className="space-y-2">
            <Label>Optional: Spindle photo(s)</Label>
            <Input type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={(e) => handleImageFiles(e.target.files, 'spindle')} />
          </div>
        </div>

        {images.length > 0 && (
          <div className="text-xs text-[var(--text-secondary)]">{images.length} image(s) attached for context</div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button className="btn btn-primary" disabled={busy} onClick={onReview}>
            Review G-Code with ChatGPT 5.1
          </button>
          <button className="btn btn-primary" disabled={busy} onClick={onOptimize}>
            Generate Optimized G-Code for My Controller
          </button>
        </div>
      </div>

      {review && (
        <div className="panel space-y-4" data-testid="gcode-review-panel">
          <div className="text-lg font-semibold">Review results</div>

          {review.raw && (
            <div className="text-sm text-[var(--text-secondary)] whitespace-pre-wrap font-mono">{review.raw}</div>
          )}

          {!review.raw && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-semibold mb-2">Detected issues</div>
                <ul className="list-disc pl-5 text-sm text-[var(--text-secondary)]">
                  {(review.detected_issues || []).map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                  {(review.detected_issues || []).length === 0 && <li>None detected</li>}
                </ul>
              </div>
              <div>
                <div className="text-sm font-semibold mb-2">Suggested fixes</div>
                <ul className="list-disc pl-5 text-sm text-[var(--text-secondary)]">
                  {(review.suggested_fixes || []).map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                  {(review.suggested_fixes || []).length === 0 && <li>No suggestions</li>}
                </ul>
              </div>
              <div>
                <div className="text-sm font-semibold mb-2">Controller compatibility warnings</div>
                <ul className="list-disc pl-5 text-sm text-[var(--text-secondary)]">
                  {(review.controller_compatibility_warnings || []).map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                  {(review.controller_compatibility_warnings || []).length === 0 && <li>None</li>}
                </ul>
              </div>
              <div>
                <div className="text-sm font-semibold mb-2">Safety concerns</div>
                <ul className="list-disc pl-5 text-sm text-[var(--text-secondary)]">
                  {(review.safety_concerns || []).map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                  {(review.safety_concerns || []).length === 0 && <li>None</li>}
                </ul>
              </div>
              <div>
                <div className="text-sm font-semibold mb-2">Efficiency improvements</div>
                <ul className="list-disc pl-5 text-sm text-[var(--text-secondary)]">
                  {(review.efficiency_improvements || []).map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                  {(review.efficiency_improvements || []).length === 0 && <li>None</li>}
                </ul>
              </div>
              <div>
                <div className="text-sm font-semibold mb-2">Reasoning</div>
                <div className="text-sm text-[var(--text-secondary)] whitespace-pre-wrap">{review.explanation_reasoning}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {optimized && (
        <div className="panel space-y-4" data-testid="gcode-optimized-panel">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-lg font-semibold">Optimized G-code</div>
            <div className="flex gap-2">
              <button
                className="btn btn-secondary"
                onClick={() => downloadText(optimized, saveFilename || 'optimized.gcode')}
              >
                Download
              </button>
              <button className="btn btn-primary" onClick={openSave}>
                Save to library
              </button>
            </div>
          </div>

          {warnings.length > 0 && (
            <div className="text-sm text-amber-300">
              <div className="font-semibold">Warnings</div>
              <ul className="list-disc pl-5">
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {changeLog.length > 0 && (
            <div className="text-sm text-[var(--text-secondary)]">
              <div className="font-semibold text-[var(--text-primary)] mb-1">Change log</div>
              <ul className="list-disc pl-5">
                {changeLog.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          <Textarea value={optimized} readOnly className="min-h-[280px] font-mono" />
        </div>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-[var(--bg-secondary)] border border-[var(--border-subtle)]">
          <DialogHeader>
            <DialogTitle>Save G-code</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Filename</Label>
              <Input value={saveFilename} onChange={(e) => setSaveFilename(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea value={saveNotes} onChange={(e) => setSaveNotes(e.target.value)} className="min-h-[100px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={busy}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
};

export default GCodeMaster;
